/**
 * STAGE 1 云端·天空 —— 直线狙弹 + 稀疏扩散（教学关）。
 * 小兵射型：单发狙（harpy）/ 3 路扩散（wisp）/ 3 连狙 + 离场环（griffon）。
 */
import * as P from '../../game/patterns.js';
import { aimOf, fireTimer, curveBullet, driftBullet, s1Mid, s1Big } from './bosses.js';

const DROP_STD = [{ type: 'P', weight: 2 }, { type: 'POINT', weight: 3 }]; // P 占比 40%
const DROP_POINT = [{ type: 'POINT', weight: 1 }];
const DROP_ELITE = [{ type: 'B', weight: 1 }, { type: 'STAR', weight: 1 }, { type: 'P', weight: 2 }]; // 精英必掉半数为 P

/** 鹰身兵：直线飞行 + 单发狙 */
function harpy(x, y, vx, vy) {
  return {
    x, y, hp: 5, r: 12, spriteKey: 's1_harpy', scoreValue: 100, drops: DROP_STD,
    script(e, dt, ctx) {
      e.x += vx * dt * 60;
      e.y += vy * dt * 60;
      if (e.y > 8 && e.y < 340 && fireTimer(e, dt, 1.8)) {
        // 风刃：一内一外两枚反向曲线弹（风的切割感，区别于直线狙）
        const aim = aimOf(ctx, e.x, e.y);
        ctx.field.spawn({ x: e.x, y: e.y, speed: 2.0, angle: aim - 0.1, r: 4, color: 'orb_red', onUpdate: curveBullet(0.5, 2) });
        ctx.field.spawn({ x: e.x, y: e.y, speed: 2.0, angle: aim + 0.1, r: 4, color: 'orb_red', onUpdate: curveBullet(-0.5, 2) });
      }
    },
  };
}

/** 云精：正弦漂移 + 3 路扩散 */
function wisp(x, y, vy) {
  return {
    x, y, hp: 8, r: 11, spriteKey: 's1_wisp', scoreValue: 150, drops: DROP_POINT,
    script(e, dt, ctx) {
      e.x += Math.sin(e.age * 2.2) * 0.8 * dt * 60;
      e.y += vy * dt * 60;
      if (e.y > 8 && e.y < 340 && fireTimer(e, dt, 2.2)) {
        P.spread(ctx.field, e.x, e.y, e.age, aimOf(ctx, e.x, e.y), { n: 3, arc: 0.5, speed: 1.9, color: 'drop_cyan', onUpdate: driftBullet(0.35, 1.8) });
      }
    },
  };
}

/** 狮鹫精英：入场悬停 3s 打 3 连狙，离场放一圈稀疏环 */
function griffon(x) {
  return {
    x, y: -24, hp: 14, r: 14, spriteKey: 's1_griffon', scoreValue: 300, drops: DROP_ELITE,
    script(e, dt, ctx) {
      if (e.y < 130) {
        e.y += 1.8 * dt * 60;
      } else if ((e._hold = (e._hold || 0) + dt) < 3) {
        e.x += Math.sin(e.age * 1.5) * 0.5 * dt * 60;
        if (fireTimer(e, dt, 1.2)) {
          P.aimed(ctx.field, e.x, e.y, e.age, aimOf(ctx, e.x, e.y), { n: 3, speed: 2.4, color: 'orb_red', jitter: 0.06 });
        }
      } else {
        if (!e._bye) {
          e._bye = true;
          P.ring(ctx.field, e.x, e.y, e.age, 0, { n: 8, speed: 1.6, color: 'drop_cyan' });
        }
        e.y += 2.4 * dt * 60;
      }
    },
  };
}

export default {
  id: 1, name: '云端·天空', bg: 'bg1', bgm: 'stage1',
  *script(api) {
    const W = api.VIEW_W;
    yield { say: 'STAGE 1  云端·天空' };
    yield { wait: 1.0 };

    // W1 纵队：引导上下走位
    yield { wave: [0, 1, 2, 3, 4].map((i) => harpy(W / 2, -24 - i * 44, 0, 1.7)) };
    yield { wait: 4.0 };
    // W2 交叉：左右斜切
    yield {
      wave: [
        ...[0, 1, 2].map((i) => harpy(40 + i * 36, -24 - i * 30, 1.0, 1.3)),
        ...[0, 1, 2].map((i) => harpy(W - 40 - i * 36, -24 - i * 30, -1.0, 1.3)),
      ],
    };
    yield { wait: 4.0 };
    // W3 云精横排：正弦下压
    yield { wave: [0, 1, 2, 3].map((i) => wisp(90 + i * 100, -24, 1.2)) };
    yield { wait: 4.0 };
    // W4 精英狮鹫 ×2
    yield { wave: [griffon(120), griffon(W - 120)] };
    yield { clear: true };
    yield { wait: 1.0 };

    yield { boss: s1Mid() };
    yield { wait: 2.0 };

    // W5 包夹：两侧横飞（道中二，强度上升）
    yield {
      wave: [
        ...[0, 1, 2].map((i) => harpy(-20, 80 + i * 40, 1.7, 0.4)),
        ...[0, 1, 2].map((i) => harpy(W + 20, 100 + i * 40, -1.7, 0.4)),
      ],
    };
    yield { wait: 4.0 };
    // W6 精英三狮鹫
    yield { wave: [griffon(100), griffon(W / 2), griffon(W - 100)] };
    yield { wait: 3.0 };
    // W7 云精 + 交叉混编
    yield {
      wave: [
        ...[0, 1, 2, 3].map((i) => wisp(70 + i * 110, -24, 1.4)),
        ...[0, 1].map((i) => harpy(60 + i * 60, -60, 0.9, 1.5)),
        ...[0, 1].map((i) => harpy(W - 60 - i * 60, -60, -0.9, 1.5)),
      ],
    };
    yield { clear: true };
    yield { wait: 1.0 };

    yield { boss: s1Big() };
    yield { say: 'STAGE 1 CLEAR' };
  },
};
